import { tzOpts } from "@/lib/tz";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from "lucide-react";

const KIND_LABEL = {
  billing_before_5: "5d antes",
  billing_on_due: "Vencimento",
  billing_after_5: "5d atraso",
  billing_after_10: "10d atraso",
  billing_after_15: "15d atraso",
  billing_after_30: "30d atraso",
  billing_after_45: "45d atraso",
  birthday: "Aniversário",
  marketing: "Marketing",
  bulk_range: "Cobrança em lote",
  manual: "Manual",
  on_request: "Sob demanda",
};

const PAGE_SIZE = 25;

export default function Messages() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/messages", {
        params: { page: p, limit: PAGE_SIZE, kind: kind === "all" ? "" : kind, search },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const runSearch = () => load(1);

  const goToPage = (p) => {
    if (p < 1 || p > pages) return;
    load(p);
  };

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Log</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Envios</h1>
        <p className="text-sm text-slate-500 mt-2">Histórico de mensagens automáticas emitidas pelo sistema.</p>
      </header>

      <Card className="bg-slate-900/50 border-slate-800 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-slate-400 text-xs uppercase tracking-[0.15em] block mb-1">Tipo</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="messages-kind-filter" className="bg-slate-950 border-slate-800 text-white h-9 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
                <SelectItem value="all">Todos os tipos</SelectItem>
                {Object.entries(KIND_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-slate-400 text-xs uppercase tracking-[0.15em] block mb-1">Buscar</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="messages-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Nome, login, número ou texto da mensagem…"
                className="bg-slate-950 border-slate-800 text-white pl-8 h-9"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-4">Data</th>
                <th className="text-left py-2 px-4">Tipo</th>
                <th className="text-left py-2 px-4">Cliente</th>
                <th className="text-left py-2 px-4">Número</th>
                <th className="text-left py-2 px-4">Prévia</th>
              </tr>
            </thead>
            <tbody data-testid="messages-tbody">
              {loading && (
                <tr><td colSpan={5} className="py-8 text-center text-slate-500">Carregando…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500">
                    {search || kind !== "all" ? "Nenhum envio encontrado com esse filtro." : "Nenhum envio ainda."}
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r._id} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-200">
                  <td className="py-2 px-4 font-mono-jb text-slate-400 text-xs whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("pt-BR", tzOpts())}
                  </td>
                  <td className="py-2 px-4">
                    <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                      {KIND_LABEL[r.kind] || r.kind}
                    </Badge>
                  </td>
                  <td className="py-2 px-4 text-white">{r.client_name || "-"}</td>
                  <td className="py-2 px-4 font-mono-jb text-slate-400">{r.number}</td>
                  <td className="py-2 px-4 text-slate-300 max-w-md truncate">{r.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 flex-wrap gap-2">
            <div className="text-xs text-slate-500">
              Página {page} de {pages} · mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="messages-page-first"
                onClick={() => goToPage(1)}
                disabled={page === 1}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="messages-page-prev"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-400 px-2 font-mono-jb">{page} / {pages}</span>
              <button
                data-testid="messages-page-next"
                onClick={() => goToPage(page + 1)}
                disabled={page === pages}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                data-testid="messages-page-last"
                onClick={() => goToPage(pages)}
                disabled={page === pages}
                className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
